import { WEB_RTC_Constants } from "./Constants";
import MediaDeviceMgr from "../../util/callutil/VideoDeviceMgr";
import { CALL_MESSAGE_TYPE } from "../../model/Constants";
import { createOption, createVideoElement } from "../../util/HTMLElements";
import { debounce } from "../../util/Misc";
import shoutUIComponent from "../../../model/shoutUIComponent";
import {deviceSettingsRender} from "../templates/renders/deviceSettings";

interface TypeDef_DeviceSettings extends shoutUIComponent {
    constants?: any;
    renderer: any;
    $app?: any;
    initializeDeviceSettings:() => void;
    initializeMediaSrcElements: () => void;
    showDevices: () => void;
    setVolume:(e: any) => void;
    unmuteVolume:() => void;
    muteVolume:() => void;
    getStartedAudioVideoCall: () => void;
    setSinkID: (sinkID: any) => void;
    setAudioVideoSource: (audioSrc: any, videoSrc: any) => void;
    setVolumeHandler:() => void;
    onClickSaveAndContinueNext: () => void;
    checkDeviceSettings : () => void;
}

/**
 * Custom element to display media devices such as microphone,camera,speaker and their settings
 * @type {{template, created(), tagName: string, onCancelClick(): void, onOkClick(): void}}
 */
const DeviceSettings: TypeDef_DeviceSettings = {
    tagName: "device-settings-view",
    renderer: deviceSettingsRender,
    created(): void {
        this.initializeDeviceSettings();
    },
    initializeDeviceSettings(): void {
        this.initializeMediaSrcElements();
        this.mediaDeviceManager = new MediaDeviceMgr(this);
        this.showDevices();
        this.setVolumeHandler();
    },
    initializeMediaSrcElements(): void {
        this.audioControl = this.el.querySelector("#vol-control");
        this.audioInputSelect = this.el.querySelector("select#audioSource");
        this.audioOutputSelect = this.el.querySelector("select#audioOutput");
        this.videoSelect = this.el.querySelector("select#videoSource");
        this.volumeContainer = this.el.querySelector(".microphone-volume-container");
        this.deviceMediaContainer = this.el.querySelector(".device-media-container");
    },
    showDevices(): void {
        /**
         * dynamically injecting video element due to issue in safari
         * @type {HTMLVideoElement}
         */
        if (this.data.showCustomerCamera) {
            this.videoElement = createVideoElement("device-settings-video", true);
            const containerElement = this.el.querySelector(".preview-container");
            containerElement.insertBefore(this.videoElement, containerElement.children[0]);
        }

        this.mediaDeviceManager.showMediaDeviceSettings(
            this.videoElement,
            this.audioInputSelect,
            this.audioOutputSelect,
            this.videoSelect,
            this.volumeContainer
        );
    },

    /**
     * change the volume level
     * @param {Event} e
     */
    setVolume(e: any): void {
        this.debouncedVolHandler(e);
    },
    unmuteVolume(): void {
        if (this.oldVolLevel) {
            this.videoElement.volume = this.oldVolLevel / 100;
            this.audioControl.value = this.oldVolLevel;
        }
    },
    muteVolume(): void {
        this.oldVolLevel = this.audioControl.value;
        this.videoElement.volume = 0.0;
        this.audioControl.value = 0;
    },
    getStartedAudioVideoCall(): void {

        if (this.data.voiceOnlyCall) {
            this.$app.sendWebRTCInvitationState(CALL_MESSAGE_TYPE.NEW_VOICE_ONLY_CALL);
        } else {
            this.$app.sendWebRTCInvitationState(CALL_MESSAGE_TYPE.NEW_CALL);
        }

        // requesting video call container to inject video call screen
        const videoContainerView: any = document.querySelector("video-container-view");
        videoContainerView
            .showVideoCallView(this.volume || 0.5, this.sinkID, this.audioSrc, this.videoSrc);
    },
    setSinkID(sinkID: any): void {
        this.sinkID = sinkID;
    },
    setAudioVideoSource(audioSrc: any, videoSrc: any): void {
        this.audioSrc = audioSrc;
        this.videoSrc = videoSrc;
    },
    removed(): void {
        this.mediaDeviceManager.clear();
    },
    setVolumeHandler(): void {
        this.debouncedVolHandler = debounce((e: any) => {
            const val = parseFloat(e.target.value);
            this.volume = val / 100;
        }, 100);
    },
    onClickSaveAndContinueNext(): void {
        const that = this;
        this.data.saveAndContinueNext = !this.data.saveAndContinueNext;

        if (!this.data.saveAndContinueNext) {
            this.checkDeviceSettings();
        } else {
            this.el.querySelector("#getStartedBtn")
                .addEventListener("click", function (e:any) {
                    that.getStartedAudioVideoCall();
                });
        }
    },
    checkDeviceSettings(): void {
        this.initializeMediaSrcElements();
        this.devices = {};

        if (this.audioSrc) {
            this.devices.audio = { deviceId: { exact: this.audioSrc } };
        }

        const src = this.devices.audio.deviceId.exact;
        this.audioInputSelect.add(createOption(src, src));

        if (this.videoSrc && this.data.showCustomerCamera) {
            this.devices.video = { deviceId: { exact: this.videoSrc } };
            const videoSrc = this.devices.video.deviceId.exact;
            this.videoSelect.add(createOption(videoSrc, videoSrc));
        }

        this.audioOutputSelect.add(createOption(this.sinkID, this.sinkID));
        this.showDevices();
    },
};

/**
 * @export
 * @param   {CIApp} appInstance App instance
 * @return  {DeviceSettings}    View instance
 */
export default (appInstance:any) => {
    const appConfig = appInstance.getUIConfig();
    const deviceSettingsProps = appConfig.webRTCProps && appConfig.webRTCProps.deviceSettingsProps;
    const imgPath = appInstance.getImagePath();
    DeviceSettings.$app = appInstance;
    const videoCallProps = deviceSettingsProps && deviceSettingsProps.videoCall;
    const voiceOnlyCallProps = deviceSettingsProps && deviceSettingsProps.voiceOnlyCall;
    const customerCameraEnable = WEB_RTC_Constants.ENABLED_CUSTOMER_CAMERA.toLowerCase();
    const voiceOnlyEnable = WEB_RTC_Constants.ENABLED_VOICE_ONLY_CALL.toLowerCase();

    /**
     * @type {Object}
     */
    const constObj: any = {};

    if (deviceSettingsProps) {
        constObj.continueBtnText = deviceSettingsProps.continueButtonText
        || WEB_RTC_Constants.DEVICE_SETTINGS_CONTINUE_BTN_TEXT;
        constObj.speakerMuteImg = imgPath + deviceSettingsProps.speakerMuteImg;
        constObj.speakerVolumeImg = imgPath + deviceSettingsProps.speakerVolumeImg;
        constObj.microphoneVolumeImg = imgPath + deviceSettingsProps.microphoneVolumeImg;
        constObj.disclaimerText = deviceSettingsProps.disclaimerText;
    }

    DeviceSettings.data = {
        saveAndContinueNext: false,
        finalText: (state: any) => {
            if (state.prop[customerCameraEnable] === "true") {
                const text = videoCallProps && videoCallProps.finalText
                    ? videoCallProps.finalText : WEB_RTC_Constants.VIDEO_CALL_DEVICE_SETTINGS_FINAL_TEXT;
                return text;
            }

            const text = voiceOnlyCallProps && voiceOnlyCallProps.finalText
                ? voiceOnlyCallProps.finalText : WEB_RTC_Constants.VOICE_ONLY_CALL_DEVICE_SETTINGS_FINAL_TEXT;
            return text;
        },
        headingText: (state: any) => {
            if (state.prop[customerCameraEnable] === "true") {
                const text = videoCallProps && videoCallProps.heading
                    ? videoCallProps.heading : WEB_RTC_Constants.VIDEO_CALL_HEADING;
                return text;
            }

            const text = voiceOnlyCallProps && voiceOnlyCallProps.heading
                ? voiceOnlyCallProps.heading : WEB_RTC_Constants.VOICE_ONLY_CALL_HEADING;
            return text;
        },
        primaryText: (state: any) => {
            if (state.prop[customerCameraEnable] === "true") {
                const text = videoCallProps && videoCallProps.primaryInstructionText
                    ? videoCallProps.primaryInstructionText : WEB_RTC_Constants.VIDEO_CALL_DEVICE_SETTINGS_PRIMARY_TEXT;
                return text;
            }

            const text = voiceOnlyCallProps && voiceOnlyCallProps.primaryInstructionText
                ? voiceOnlyCallProps.primaryInstructionText : WEB_RTC_Constants.VOICE_CALL_DEVICE_SETTINGS_PRIMARY_TEXT;
            return text;
        },

        checkDeviceSettingsBtnText: (state: any) => {
            if (state.prop[customerCameraEnable] === "true") {
                const text = videoCallProps && videoCallProps.checkDeviceSettingsText
                    ? videoCallProps.checkDeviceSettingsText : WEB_RTC_Constants.VIDEO_CALL_CHECK_DEVICES_BUTTON_TEXT;
                return text;
            }

            const text = voiceOnlyCallProps && voiceOnlyCallProps.checkDeviceSettingsText
                ? voiceOnlyCallProps.checkDeviceSettingsText : WEB_RTC_Constants.VOICE_ONLY_CALL_CHECK_DEVICES_TEXT;
            return text;
        },
        secondaryText: (state: any) => {
            if (state.prop[customerCameraEnable] === "true") {
                const text = videoCallProps && videoCallProps.secondaryInstructionText
                    ? videoCallProps.secondaryInstructionText : WEB_RTC_Constants.VIDEO_CALL_DEVICE_SETTINGS_SECONDARY_TEXT;
                return text;
            }

            const text = voiceOnlyCallProps && voiceOnlyCallProps.secondaryInstructionText
                ? voiceOnlyCallProps.secondaryInstructionText : WEB_RTC_Constants.VOICE_CALL_DEVICE_SETTINGS_SECONDARY_TEXT;
            return text;
        },
        startButtonText: (state: any) => {
            if (state.prop[customerCameraEnable] === "true") {
                const text = videoCallProps && videoCallProps.startButtonText
                    ? videoCallProps.startButtonText : WEB_RTC_Constants.VIDEO_CALL_START_BUTTON_TEXT;
                return text;
            }

            const text = voiceOnlyCallProps && voiceOnlyCallProps.startButtonText
                ? voiceOnlyCallProps.startButtonText : WEB_RTC_Constants.VOICE_ONLY_CALL_START_BUTTON_TEXT;
            return text;
        },
        showCustomerCamera:
                (state: any) => state.prop[customerCameraEnable] === "true",
        voiceOnlyCall:
                (state: any) => state.prop[voiceOnlyEnable] === "true",
    };

    DeviceSettings.constants = constObj;
    return DeviceSettings;
};
